import { useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import { setupCanvas } from '@/utils/canvasHelpers';
import type { LandmarkDeviationWithCoords } from '@/types/visualization';
import {
  getDeviationColor,
  RENDERING,
  CANVAS_DRAWING,
  getOpacityFromIntensity,
} from '@/utils/visualizationConstants';

interface DeviationVectorsOverlayProps {
  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
  landmarkDeviations: LandmarkDeviationWithCoords[];
  /** Width of the actual video render area */
  width: number;
  /** Height of the actual video render area */
  height: number;
  /** X offset from canvas left edge to the video render area (pillarbox offset) */
  offsetX?: number;
  /** Y offset from canvas top edge to the video render area (letterbox offset) */
  offsetY?: number;
  /** Full container width — canvas is sized to this */
  containerWidth?: number;
  /** Full container height — canvas is sized to this */
  containerHeight?: number;
  intensity: number;
  currentFrame: number;
  showOnlySignificant?: boolean;
  minDeviationThreshold?: number;
  className?: string;
}

/**
 * Draw an arrow from one point to another
 */
function drawArrow(
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  color: string,
  headLength: number = CANVAS_DRAWING.ARROW_HEAD_LENGTH
) {
  const angle = Math.atan2(toY - fromY, toX - fromX);

  // Draw line
  ctx.strokeStyle = color;
  ctx.lineWidth = CANVAS_DRAWING.LINE_WIDTH;
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();

  // Draw arrow head
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(
    toX - headLength * Math.cos(angle - Math.PI / 6),
    toY - headLength * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    toX - headLength * Math.cos(angle + Math.PI / 6),
    toY - headLength * Math.sin(angle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();
}

/**
 * DeviationVectorsOverlay Component
 *
 * Draws arrows from athlete landmark positions to where they should be (template positions).
 * Arrow color indicates severity: green (small) -> yellow -> red (large deviation).
 */
function DeviationVectorsOverlay({
  canvasRef: externalCanvasRef,
  landmarkDeviations,
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  containerWidth,
  containerHeight,
  intensity,
  currentFrame,
  showOnlySignificant = false,
  minDeviationThreshold = 0.0,
  className,
}: DeviationVectorsOverlayProps) {
  const internalCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const canvasW = containerWidth ?? width;
  const canvasH = containerHeight ?? height;

  useEffect(() => {
    const ctx = setupCanvas(canvasRef, canvasW, canvasH);
    if (!ctx) return;

    try {
      // Skip rendering if intensity is too low (nearly invisible)
      if (intensity < RENDERING.MIN_INTENSITY) return;

      // Filter deviations for current frame
    const currentDeviations = (landmarkDeviations ?? []).filter(
      (dev) => dev.frame_index === currentFrame
    );

    // Apply filters
    let filteredDeviations = currentDeviations;

    // Filter by minimum threshold
    if (minDeviationThreshold > 0) {
      filteredDeviations = filteredDeviations.filter(
        (dev) => (dev.deviation_distance ?? 0) >= minDeviationThreshold
      );
    }

    // Filter by significance if requested
    if (showOnlySignificant) {
      filteredDeviations = filteredDeviations.filter((dev) => dev.is_significant);
    }

    // Draw arrows
    filteredDeviations.forEach((deviation) => {
      const {
        athlete_x,
        athlete_y,
        template_x,
        template_y,
        deviation_distance,
      } = deviation;

      // Skip if coordinates are missing
      if (
        athlete_x === undefined ||
        athlete_y === undefined ||
        template_x === undefined ||
        template_y === undefined
      ) {
        return;
      }

      // Convert normalized coordinates to canvas coordinates (apply video render area offset)
      const fromX = athlete_x * width + offsetX;
      const fromY = athlete_y * height + offsetY;
      const toX = template_x * width + offsetX;
      const toY = template_y * height + offsetY;

      // Get color based on deviation
      const color = getDeviationColor(deviation_distance ?? 0);

      // Apply intensity as alpha
      ctx.globalAlpha = getOpacityFromIntensity(intensity);

      // Draw the arrow
      drawArrow(ctx, fromX, fromY, toX, toY, color);

      // Reset alpha
      ctx.globalAlpha = 1.0;
    });
    } catch (error) {
      console.error('[DeviationVectorsOverlay] Canvas drawing error:', error);
    }
  }, [
    landmarkDeviations,
    width,
    height,
    offsetX,
    offsetY,
    canvasW,
    canvasH,
    intensity,
    currentFrame,
    showOnlySignificant,
    minDeviationThreshold,
    canvasRef,
  ]);

  // Always render canvas - the useEffect handles drawing to whichever ref is used
  return (
    <canvas
      ref={canvasRef}
      width={canvasW}
      height={canvasH}
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{ width: '100%', height: '100%' }}
      aria-label="Deviation vectors overlay"
    />
  );
}

export default DeviationVectorsOverlay;
